import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Loader, AlertCircle, ShieldClose, Clock, Ban, WifiOff } from 'lucide-react';
import {
  resolveBookingToken,
  fetchBookingById,
  fetchBookingItems,
  fetchInventoryItemsForBooking,
  fetchProductTypesByIds,
  fetchMerchant,
  fetchMerchantConfig,
  fetchLocation,
  submitCompleteReturn,
  callWebhook,
  isTransientNetworkError
} from './utils/supabaseClickShare';
import {
  getDefaultConfiguration,
  buildReturnFlow,
  getCompletedSections
} from './utils/configHelpers';
import ConfigValidator from './utils/configValidator';
import { t } from './utils/i18n';
import { injectMerchantColors, getMerchantButtonClasses, getMerchantSecondaryColor } from './utils/merchantColors';
import DynamicPhotoUpload from './components/DynamicPhotoUpload';
import DynamicQuestionSet from './components/DynamicQuestionSet';
import ItemLevelFlow from './components/ItemLevelFlow';
import DynamicThankYou from './components/DynamicThankYou';

/**
 * Detect language from browser settings. Returns 'de' if the browser
 * prefers German, otherwise falls back to 'en'.
 */
function detectBrowserLanguage() {
  try {
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    return browserLang.startsWith('de') ? 'de' : 'en';
  } catch {
    return 'en';
  }
}

/**
 * Error codes returned by token resolution
 */
const TOKEN_ERROR_CODES = {
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_REVOKED: 'TOKEN_REVOKED',
};

const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

/**
 * Main App Component for Click&Share v2 Return App
 *
 * Flow:
 * 1. Read token from /b/:token route
 * 2. Resolve token → booking_id (via hashed lookup)
 * 3. Load booking, merchant, location, config, items
 * 4. Derive language from booking.language
 * 5. Build dynamic flow based on configuration
 * 6. Guide user through sections
 * 7. Submit complete return
 */
const App = () => {
  const { token } = useParams();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  // Language — browser-detected initially, overridden by booking.language after resolution
  const [language, setLanguage] = useState(() => detectBrowserLanguage());

  // Data from database
  const [booking, setBooking] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [merchantConfig, setMerchantConfig] = useState(null);
  const [location, setLocation] = useState(null);

  // Flow state
  const [flow, setFlow] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const navigatingBackRef = useRef(false);

  // Collected data. Booking-level photos are keyed by photo section id so
  // multiple photo_upload sections are independent — each must satisfy its
  // own min_photos with its own uploads.
  const [photosBySection, setPhotosBySection] = useState({});
  const [bookingLevelAnswers, setBookingLevelAnswers] = useState({});
  const [itemReturns, setItemReturns] = useState([]);
  const [itemPhotos, setItemPhotos] = useState({});

  // Submission state
  const [submissionComplete, setSubmissionComplete] = useState(false);

  /**
   * Initialize app on mount
   */
  useEffect(() => {
    initializeApp();
  }, []);

  // Track section changes
  useEffect(() => {
    if (flow.length > 0) {
      console.log('[App] Current section index changed to:', currentSectionIndex, 'Section:', flow[currentSectionIndex]?.type);
    }
  }, [currentSectionIndex, flow]);

  // Auto-retry init when the tab becomes visible again after a network failure.
  // iOS Safari aborts in-flight fetches when a tab is backgrounded, surfacing
  // as `TypeError: Load failed`; retrying on resume recovers silently.
  useEffect(() => {
    if (errorCode !== NETWORK_ERROR_CODE) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        initializeApp();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [errorCode]);

  // Detect page reloads
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.warn('[App] Page is reloading/unloading');
    };

    const handleError = (event) => {
      console.error('[App] Global error:', event.error || event.message);
    };

    const handleUnhandledRejection = (event) => {
      console.error('[App] Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const initializeApp = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorCode(null);

      if (!token) {
        const err = new Error('No token provided');
        err.code = TOKEN_ERROR_CODES.TOKEN_INVALID;
        throw err;
      }

      // 1. Resolve token → booking_id
      const { booking_id } = await resolveBookingToken(token);
      console.log('[App] Token resolved to booking_id:', booking_id);

      // 2. Fetch booking and items in parallel
      const [bookingData, allItems] = await Promise.all([
        fetchBookingById(booking_id),
        fetchBookingItems(booking_id)
      ]);

      if (!bookingData) {
        throw new Error('Booking not found');
      }

      setBooking(bookingData);

      // 3. Derive language from booking
      const lang = bookingData.language || 'de';
      setLanguage(lang);

      // 4. Fetch merchant, config, and location in parallel (derived from booking)
      const [merchantData, config, locationData] = await Promise.all([
        fetchMerchant(bookingData.merchant_id),
        fetchMerchantConfig(bookingData.merchant_id).catch(() => null),
        fetchLocation(bookingData.location_id)
      ]);

      setMerchant(merchantData);
      setLocation(locationData);

      // Inject merchant colors into CSS
      injectMerchantColors(merchantData);

      console.log('[App] Booking loaded:', bookingData.id, '| Language:', lang, '| Merchant:', merchantData?.business_name);

      // Set Sentry context
      Sentry.setContext('booking', {
        bookingId: bookingData?.id,
        merchantId: merchantData?.id,
        merchantName: merchantData?.business_name,
      });

      // 5. Validate and set merchant config
      let merchantConfigResolved = config;
      if (!merchantConfigResolved) {
        console.warn('No merchant config found, using defaults');
        merchantConfigResolved = getDefaultConfiguration();
      }

      // Only structural errors discard the config; translation gaps are
      // warnings and are handled at render time via text fallbacks.
      const { errors: validationErrors, warnings: validationWarnings } =
        ConfigValidator.validateFlowConfig(merchantConfigResolved);
      if (validationWarnings.length > 0) {
        console.warn('Configuration validation warnings:', validationWarnings);
      }
      if (validationErrors.length > 0) {
        console.error('Configuration validation errors:', validationErrors);
        Sentry.captureMessage('Merchant config failed validation, using default flow', {
          level: 'error',
          extra: {
            merchantId: merchantData?.id,
            errors: validationErrors,
          },
        });
        merchantConfigResolved = getDefaultConfiguration();
      }

      setMerchantConfig(merchantConfigResolved);

      // 6. Filter booking items (remove addons)
      const nonAddonItems = allItems.filter(item => item.product_type !== 'addon');

      console.log(`[App] Filtered ${nonAddonItems.length} items from ${allItems.length} total (excluded ${allItems.length - nonAddonItems.length} addons)`);

      // 7. Resolve product types and exclude pool/unlimited items, then fetch inventory
      const isPoolOrUnlimited = (value) => value === 'pool' || value === 'unlimited';
      let items = nonAddonItems;

      if (nonAddonItems.length > 0) {
        const productTypeIds = nonAddonItems
          .map(item => item.product_type_id)
          .filter(Boolean);

        const productTypes = await fetchProductTypesByIds(productTypeIds);
        const poolUnlimitedProductTypeIds = new Set(
          productTypes
            .filter(pt => isPoolOrUnlimited(pt.inventory_type))
            .map(pt => pt.id)
        );

        items = nonAddonItems.filter(item =>
          !poolUnlimitedProductTypeIds.has(item.product_type_id)
        );
      }

      if (items.length > 0) {
        const inventory = await fetchInventoryItemsForBooking(items);
        setInventoryItems(inventory);
      }

      setBookingItems(items);

      // 8. Build dynamic flow
      const dynamicFlow = buildReturnFlow(merchantConfigResolved, bookingData, items);
      setFlow(dynamicFlow);

      console.log('[App] Flow built with sections:', dynamicFlow.map(s => s.id));

      setLoading(false);
    } catch (err) {
      console.error('[App] Error initializing app:', err);

      const resolvedCode = err.code
        || (isTransientNetworkError(err) ? NETWORK_ERROR_CODE : 'UNKNOWN');

      // Capture in Sentry — but do NOT include raw token
      Sentry.captureException(err, {
        tags: {
          phase: 'initialization',
          errorCode: resolvedCode,
        },
        extra: {
          userAgent: navigator.userAgent,
          online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        },
      });

      setErrorCode(resolvedCode === 'UNKNOWN' ? null : resolvedCode);
      setError(err.message || 'Failed to load booking data');
      setLoading(false);
    }
  };

  /**
   * Handle photo section complete
   */
  const handlePhotosComplete = (sectionId, photos) => {
    if (navigatingBackRef.current) {
      return;
    }
    const updated = { ...photosBySection, [sectionId]: photos };
    setPhotosBySection(updated);
    moveToNextSection({ photosBySection: updated });
  };

  /**
   * Handle booking-level questions complete
   */
  const handleBookingQuestionsComplete = (answers) => {
    if (navigatingBackRef.current) {
      return;
    }
    setBookingLevelAnswers(answers);
    moveToNextSection({ bookingLevelAnswers: answers });
  };

  /**
   * Handle item-level questions complete
   */
  const handleItemQuestionsComplete = ({ itemReturns: returns, itemPhotos: photos }) => {
    if (navigatingBackRef.current) {
      return;
    }
    setItemReturns(returns);
    setItemPhotos(photos);
    moveToNextSection({ itemReturns: returns, itemPhotos: photos });
  };

  /**
   * Move to next section in flow
   */
  const moveToNextSection = (freshData = {}) => {
    if (navigatingBackRef.current) {
      return;
    }

    const nextIndex = currentSectionIndex + 1;

    if (nextIndex >= flow.length) {
      handleSubmit(freshData);
    } else {
      const nextSection = flow[nextIndex];
      if (nextSection.type === 'completion') {
        handleSubmit(freshData);
      } else {
        setCurrentSectionIndex(nextIndex);
      }
    }
  };

  /**
   * Move to previous section in flow
   */
  const moveToPreviousSection = () => {
    if (currentSectionIndex > 0) {
      navigatingBackRef.current = true;

      let targetIndex = currentSectionIndex - 1;

      // Skip sections with no questions
      while (targetIndex >= 0) {
        const targetSection = flow[targetIndex];
        if (targetSection.type === 'question_set' &&
            targetSection.config?.scope === 'booking' &&
            (!targetSection.config?.questions || targetSection.config.questions.length === 0)) {
          targetIndex--;
        } else {
          break;
        }
      }

      if (targetIndex >= 0) {
        setCurrentSectionIndex(targetIndex);
      }

      setTimeout(() => {
        navigatingBackRef.current = false;
      }, 100);
    }
  };

  /**
   * Handle complete return submission
   */
  const handleSubmit = async (freshData = {}) => {
    try {
      setSubmitting(true);
      setError(null);

      const dataToSubmit = {
        photosBySection: freshData.photosBySection ?? photosBySection,
        bookingLevelAnswers: freshData.bookingLevelAnswers ?? bookingLevelAnswers,
        itemReturns: freshData.itemReturns ?? itemReturns,
        itemPhotos: freshData.itemPhotos ?? itemPhotos
      };

      const completedSections = getCompletedSections(
        flow,
        dataToSubmit.bookingLevelAnswers,
        dataToSubmit.itemReturns,
        dataToSubmit.photosBySection
      );

      // Flatten the per-section photos in flow order, tagging each photo with
      // its originating section so the record shows which requirement it met.
      const bookingPhotoEntries = flow
        .filter(s => s.type === 'photo_upload')
        .flatMap(s => (dataToSubmit.photosBySection[s.id] || []).map(file => ({
          file,
          sectionId: s.id
        })));

      const result = await submitCompleteReturn({
        booking,
        merchantConfig,
        completedSections,
        bookingLevelAnswers: dataToSubmit.bookingLevelAnswers,
        itemReturns: dataToSubmit.itemReturns,
        bookingPhotos: bookingPhotoEntries,
        itemPhotos: dataToSubmit.itemPhotos
      });

      console.log('[App] Return submitted successfully:', result.bookingReturn.id);

      // Optionally call webhook (don't block on failure)
      try {
        await callWebhook(booking.order_id, location?.location_name, 'completed');
      } catch (webhookError) {
        console.warn('[App] Webhook call failed (non-critical):', webhookError);
      }

      setSubmissionComplete(true);
      setSubmitting(false);

      // Move to thank you section
      const thankYouIndex = flow.findIndex(s => s.type === 'completion');
      if (thankYouIndex !== -1) {
        setCurrentSectionIndex(thankYouIndex);
      }

    } catch (err) {
      console.error('[App] Error submitting return:', err);

      const isNetwork = err.network === true || isTransientNetworkError(err);
      const resolvedCode = isNetwork ? NETWORK_ERROR_CODE : (err.code || 'UNKNOWN');

      Sentry.captureException(err, {
        tags: {
          phase: 'submission',
          errorCode: resolvedCode,
        },
        extra: {
          bookingId: booking?.id,
          partial: err.partial === true,
          failedPhotoCount: err.failedPhotoCount,
          totalPhotoCount: err.totalPhotoCount,
          online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        },
      });

      let userMessage;
      if (err.partial && err.failedPhotoCount) {
        userMessage = t(language, 'submitPhotosFailed', err.failedPhotoCount);
      } else if (isNetwork) {
        userMessage = t(language, 'submitNetworkFailed');
      } else {
        userMessage = t(language, 'submitError');
      }

      setErrorCode(resolvedCode === 'UNKNOWN' ? null : resolvedCode);
      setError(userMessage);
      setSubmitting(false);
    }
  };

  // ===========================================================================
  // RENDER: Loading
  // ===========================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-pink-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">
            {t(language, 'loadingBooking')}
          </p>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // RENDER: Token / initialization errors
  // ===========================================================================
  if (error && !booking) {
    // Token-specific error states
    if (errorCode === TOKEN_ERROR_CODES.TOKEN_INVALID) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <ShieldClose className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t(language, 'errInvalidLinkTitle')}
            </h2>
            <p className="text-gray-600">
              {t(language, 'errInvalidLinkBody')}
            </p>
          </div>
        </div>
      );
    }

    if (errorCode === TOKEN_ERROR_CODES.TOKEN_EXPIRED) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t(language, 'errExpiredTitle')}
            </h2>
            <p className="text-gray-600">
              {t(language, 'errExpiredBody')}
            </p>
          </div>
        </div>
      );
    }

    if (errorCode === NETWORK_ERROR_CODE) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <WifiOff className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t(language, 'errNetworkTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t(language, 'errNetworkBody')}
            </p>
            <button
              onClick={initializeApp}
              className="w-full px-6 py-3 rounded-lg font-semibold bg-rose-500 text-white hover:bg-rose-600 transition-colors"
            >
              {t(language, 'tryAgain')}
            </button>
          </div>
        </div>
      );
    }

    if (errorCode === TOKEN_ERROR_CODES.TOKEN_REVOKED) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <Ban className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t(language, 'errRevokedTitle')}
            </h2>
            <p className="text-gray-600">
              {t(language, 'errRevokedBody')}
            </p>
          </div>
        </div>
      );
    }

    // Generic error
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t(language, 'errGenericTitle')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-6 py-3 rounded-lg font-semibold bg-rose-500 text-white hover:bg-rose-600 transition-colors"
          >
            {t(language, 'tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // RENDER: Submitting
  // ===========================================================================
  if (submitting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader
            className="w-12 h-12 animate-spin mx-auto mb-4"
            style={{ color: merchant?.color2 || '#ec4899' }}
          />
          <p className="text-gray-600 text-lg">
            {t(language, 'submitting')}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {t(language, 'pleaseWait')}
          </p>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // RENDER: Submission error (booking is loaded but submit failed)
  // ===========================================================================
  if (error && booking && !submissionComplete) {
    const isNetwork = errorCode === NETWORK_ERROR_CODE;
    const Icon = isNetwork ? WifiOff : AlertCircle;
    const iconColor = isNetwork ? 'text-amber-500' : 'text-red-500';
    const heading = isNetwork
      ? t(language, 'errNetworkTitle')
      : t(language, 'errSubmitFailedTitle');

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Icon className={`w-16 h-16 ${iconColor} mx-auto mb-4`} />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{heading}</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => handleSubmit()}
            className="w-full px-6 py-3 rounded-lg font-semibold bg-rose-500 text-white hover:bg-rose-600 transition-colors"
          >
            {t(language, 'tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // RENDER: No configuration
  // ===========================================================================
  if (flow.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {t(language, 'errNoConfigTitle')}
          </h2>
          <p className="text-gray-600">
            {t(language, 'errNoConfigBody')}
          </p>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // RENDER: Current flow section
  // ===========================================================================
  const currentSection = flow[currentSectionIndex];

  // Advancing from a section triggers submission when the next section is the
  // completion screen (or there is none) — see moveToNextSection. Only then
  // should a question set's button read "Complete"; otherwise more steps
  // follow and it should read "Next".
  const nextSection = flow[currentSectionIndex + 1];
  const isLastSection = !nextSection || nextSection.type === 'completion';

  switch (currentSection.type) {
    case 'photo_upload':
      // key forces a remount per section — without it React reuses the
      // component instance across consecutive photo sections and its internal
      // photo state (initialized only on mount) would leak between them.
      return (
        <DynamicPhotoUpload
          key={currentSection.id}
          config={currentSection.config}
          language={language}
          merchant={merchant}
          initialPhotos={photosBySection[currentSection.id] || []}
          onPhotosChange={(photos) => setPhotosBySection(prev => ({
            ...prev,
            [currentSection.id]: photos
          }))}
          onComplete={(photos) => handlePhotosComplete(currentSection.id, photos)}
          onBack={currentSectionIndex > 0 ? moveToPreviousSection : null}
        />
      );

    case 'question_set':
      if (currentSection.config.scope === 'booking') {
        return (
          <DynamicQuestionSet
            config={currentSection.config}
            language={language}
            merchant={merchant}
            onComplete={handleBookingQuestionsComplete}
            onBack={currentSectionIndex > 0 ? moveToPreviousSection : null}
            isLastSection={isLastSection}
          />
        );
      } else if (currentSection.config.scope === 'item') {
        return (
          <ItemLevelFlow
            config={currentSection.config}
            language={language}
            merchant={merchant}
            bookingItems={bookingItems}
            inventoryItems={inventoryItems}
            onComplete={handleItemQuestionsComplete}
            onBack={moveToPreviousSection}
            isLastSection={isLastSection}
          />
        );
      }
      break;

    case 'completion':
      return (
        <DynamicThankYou
          config={currentSection.config}
          language={language}
          merchant={merchant}
          googleMapsUrl={location?.google_maps_url}
          locationId={location?.id}
        />
      );

    default:
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-600">Unknown section type: {currentSection.type}</p>
        </div>
      );
  }
};

export default App;
